import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { authViewTransition, cn } from '@/lib/utils'
import { Button, ButtonLink } from '@/components/ui/button'
import { LogoIcon } from '@/routes/_app/-components/shared/logo'
import { CompactLanguageSelect } from './footer'
import { DemoChevron } from './demo-chevron'
import { FEATURE_FLAGS } from './feature-flags'
import { LandingMobileMenu } from './mobile-menu'

// The nav's sections, in page order; the plans only while pricing is on.
export const SECTION_IDS = (['professionals', 'solutions', 'api', 'plans', 'faq'] as const).filter(
  (id) => FEATURE_FLAGS.showPricing || id !== 'plans',
)

/** `<html data-landing>` while the landing's nav is mounted: app.css glides the document between sections on it. */
function markLandingDocument(node: HTMLElement | null) {
  if (!node) return
  document.documentElement.setAttribute('data-landing', '')
  return () => document.documentElement.removeAttribute('data-landing')
}

/** The section being read: the last one whose top has passed a third of the viewport (the last one at the page's end). */
function useActiveSection() {
  const [active, setActive] = useState<string>()
  useEffect(() => {
    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const line = window.innerHeight / 3
        const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
        let current: string | undefined
        for (const id of SECTION_IDS) {
          const top = document.getElementById(id)?.getBoundingClientRect().top
          if (top !== undefined && top <= line) current = id
        }
        setActive(atEnd ? SECTION_IDS.at(-1) : current)
      })
    }
    measure()
    window.addEventListener('scroll', measure, { passive: true })
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', measure)
      window.removeEventListener('resize', measure)
    }
  }, [])
  return active
}

/** Below `sm` the full label cannot sit beside the logo and the burger without the bar scrolling sideways. */
function ContactLabel() {
  const { t } = useLingui()
  return (
    <>
      <span className="sm:hidden">{t`Contact`}</span>
      <span className="hidden sm:inline">{t`Get in touch`}</span>
    </>
  )
}

/**
 * The marketing top bar. `onContact` is the landing's own scroll-to-the-form, and it is what tells the two apart: with
 * it the bar carries the page's sections and reads which one is being read; without it (the docs and policy pages,
 * which have no sections of their own) the bar is logo, language, sign in, and a CTA that goes home to the form.
 */
export function LandingNav(props: { onContact?: () => void }) {
  const { t } = useLingui()
  // The section sells a platform integration, so the nav names that rather than the interface behind it. Its id
  // stays `api`, which every anchor on the page already points at.
  const labels = {
    professionals: t`Professionals`,
    solutions: t`Solutions`,
    api: t`Integration`,
    plans: t`Plans`,
    faq: t`FAQ`,
  }
  const active = useActiveSection()
  // Built once and handed to both faces of the bar, so the links can't drift between desktop and the sheet.
  const sections = props.onContact ? SECTION_IDS.map((id) => ({ id, label: labels[id] })) : []

  return (
    <header ref={markLandingDocument} className="bg-background sticky top-0 z-50">
      {/* The bottom rule spans the content column, inset to the same gutters as the Solutions grid below. */}
      <div className="fade-rule-bottom mx-auto flex h-17 max-w-6xl items-center gap-2 px-5 [--fade-rule-inset:1.25rem] sm:gap-4 sm:px-6 sm:[--fade-rule-inset:1.5rem] lg:gap-8">
        <Link to="/" className="-ml-3 shrink-0 p-3" title="D'Anchiano">
          <LogoIcon className="h-auto w-28 sm:w-[10.25rem]" />
        </Link>
        {props.onContact ? (
          <nav className="hidden items-center text-sm font-medium md:flex">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                aria-current={active === section.id ? 'location' : undefined}
                className={cn(
                  'hover:text-foreground flex items-center py-3 px-2.5 transition-colors',
                  active === section.id ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {section.label}
              </a>
            ))}
          </nav>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <CompactLanguageSelect className="hidden sm:flex" />
          <ButtonLink
            variant="outline"
            size="lg"
            to="/login"
            viewTransition={authViewTransition('enter')}
            className="hidden px-3.5 lg:inline-flex"
          >
            {t`Sign in`}
          </ButtonLink>
          {/* Kept at every width, beside the burger: it is the page's one conversion action, and burying the
              only way to ask behind a menu costs more than the gutter it takes. */}
          {props.onContact ? (
            <Button size="lg" type="button" className="px-3.5" onClick={props.onContact}>
              <ContactLabel />
              <DemoChevron />
            </Button>
          ) : (
            <ButtonLink size="lg" to="/" hash="contact" className="px-3.5">
              <ContactLabel />
              <DemoChevron />
            </ButtonLink>
          )}
          <LandingMobileMenu sections={sections} active={active} onContact={props.onContact} />
        </div>
      </div>
    </header>
  )
}
